import { reject, mapKeys, sortBy, size, chunk, compact, uniq, keys } from 'lodash';
import * as firebase from 'firebase/app';
import shuffleSeed from 'shuffle-seed';
import { allTiles, idToTileMap } from './tiles';
import { Command, BoardState, User } from '../types';

const startingTiles = 21;

let tiles: typeof allTiles = [];
// @ts-ignore
window.tiles = () => tiles;

export const peel = ({
    userId,
    userIds,
    seed,
}: {
    userId: number | string,
    userIds: (number | string)[],
    seed: number,
}) => {
    const tilesToDeal = tiles.splice(0, size(userIds));
    // Always give to the requesting user first
    const sortedUsers = [
        userId,
        ...shuffleSeed.shuffle(
            reject(userIds, (id) => id === userId),
            seed,
        ),
    ];
    return mapKeys(tilesToDeal, (_key, idx) => sortedUsers[idx]);
};

export const dump = ({
    userId,
    tileToDump,
    seed,
}: {
    userId: number | string,
    tileToDump: number,
    seed: number,
}) => {
    // Grab new tiles
    const tilesToDeal = tiles.splice(0, 3);
    // Put the old tile in and shuffle
    tiles = shuffleSeed.shuffle(
        [...tiles, { id: tileToDump, letter: idToTileMap[tileToDump] }],
        seed,
    );
    return {
        [userId]: tilesToDeal,
    };
};

export const split = (userIds: (string | number)[], seed: number) => {
    // First, shuffle it
    tiles = shuffleSeed.shuffle(allTiles, seed);

    const numTilesToRemove = startingTiles * size(userIds);

    if (numTilesToRemove > size(tiles)) {
        throw new Error(`Cannot draw ${numTilesToRemove} from deck`);
    } else {
        const tilesToDeal = tiles.splice(0, numTilesToRemove);
        const hands = chunk(tilesToDeal, startingTiles);
        const sortedUsers = sortBy(userIds.slice(0));
        const newHands = mapKeys(hands, (_key, idx) => sortedUsers[idx]);
        return newHands;
    }
};

export const addCommand = (roomId: string, command: Command) => {
    const database = firebase.database();
    const commandsRef = database.ref(`rooms/${roomId}/commands`);
    commandsRef.push(command);
};

export const addBoard = ({
    roomId,
    userId,
    board,
}: {
    roomId: string,
    userId: string,
    board: BoardState,
}) => {
    const database = firebase.database();
    const boardRef = database.ref(`rooms/${roomId}/board/${userId}`);
    boardRef.set(board);
};

export const approveBoard = ({ userId, roomId }: { userId: string, roomId: string }) => {
    const database = firebase.database();
    const winnerRef = database.ref(`rooms/${roomId}/winner/`);
    winnerRef.set(userId);
};

export const rejectBoard = ({ roomId }: { roomId: string }) => {
    const database = firebase.database();
    const boardRef = database.ref(`rooms/${roomId}/board/`);
    boardRef.remove();
};

export const getTiles = () => tiles;

export const getUser = ({ roomId, userId }: { roomId: string, userId: string }): Promise<User> => {
    const database = firebase.database();
    const userRef = database.ref(`rooms/${roomId}/users/${userId}`);

    return userRef.once('value').then((val) => {
        const userName = val.val();
        return {
            userId,
            userName,
        };
    });
};

export const updateHandSize = ({
    roomId,
    userId,
    handSize,
}: {
    roomId: string,
    userId: string,
    handSize: number,
}) => {
    const database = firebase.database();
    const handRef = database.ref(`rooms/${roomId}/handSizes/${userId}`);
    handRef.set(handSize);
};

